import React, { useCallback, useRef, useState } from "react";
import Quagga from "quagga";

const BarcodeScanner = ({ onScan, onError }) => {
  const videoRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);

  const stopScanning = useCallback(() => {
    if (!isScanning) return;

    Quagga.stop();
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
  }, [isScanning]);

  const startScanning = useCallback(async () => {
    if (isScanning) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 780 },
          height: { ideal: 720 }
        }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      Quagga.init({
        inputStream: {
          name: "Live",
          type: "LiveStream",
          target: videoRef.current,
          constraints: {
            facingMode: "environment"
          },
        },
        decoder: {
          readers: [
            "ean_reader",
            "ean_8_reader",
            "code_128_reader",
            "code_39_reader",
            "upc_reader",
            "upc_e_reader"
          ],
          debug: {
            showCanvas: true,
            showPatches: true,
            showFoundPatches: true,
            showSkeleton: true,
            showLabels: true,
            showPatchLabels: true,
            showRemainingPatchLabels: true,
            boxFromPatches: {
              showTransformed: true,
              showTransformedBox: true,
              showBB: true
            }
          }
        }
      }, (err) => {
        if (err) {
          onError?.(err);
          return;
        }

        setIsScanning(true);
        Quagga.start();
      });

      Quagga.onDetected((result) => {
        if (result?.codeResult?.code) {
          onScan(result.codeResult.code);
          stopScanning();
        }
      });

    } catch (err) {
      onError?.(err);
      console.error("Error starting scanner:", err);
    }
  }, [isScanning, onError, onScan, stopScanning]);

  React.useLayoutEffect(() => {
    startScanning();
    return () => {
      stopScanning();
    };
  }, [startScanning, stopScanning]);

  return (
    <div className="relative w-full h-[400px] bg-black">
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          autoPlay
          playsInline
          muted
          style={{width:"100%"}}
        />
        <div id="interactive" className="viewport absolute inset-0" />
      </div>

      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4/5 h-16 border-2 border-red-500 rounded-lg pointer-events-none" />
    </div>
  );
};

export default BarcodeScanner;